import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useState
} from 'react';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { NewSection } from '../../NewSection';
import { CardsLayout } from '../../ui/CardsLayout';
import { ItemCard } from '../../ui/ItemCard';
import { SkeletonLoaders } from '../SkeletonLoaders';
import { ParallaxGapBanner } from '../ParallaxGapBanner';
import { PARALLAX_GAP_LIST_INDEX } from '../ParallaxGapBanner/constants';
import { withEndlessScroll } from '../../HOC/endlessScroll';
import { useBookmarks } from '../../../hooks/useBookmarks';
import { useClient } from '../../../hooks/useClient';
import useScreenSize from '../../../hooks/useScreenSize';
import { useGetIsMapView } from '../../../hooks/useGetIsMapView';
import { useInitialPriceView } from '../../../hooks/usePriceView';
import { selectSearchFormCurrentState } from '../../../store/selectors';
import { checkIsCategorySupportsPriceToggle } from '../../../utils/helpers/common';
import { SEARCH_RESULT_ROUTES } from '../../../utils/helpers/constants';
import { searchItemsQuery } from '../../../api/queries';
import { SearchItemsQuery } from '../../../graphql/graphql';
import { ItemConnectionSort } from '../../../graphql/types';
import { MapViewQueryParam } from '../../ItemsMap/types';
import { SearchResultsItemsMap } from '../../NewSearch/SearchResultsItemsMap';
import { SortingFilter } from '../../NewSearch/NewSearchBar/SortingFilter';
import { ItemsViewToggleLink } from '../../NewSearch/NewSearchBar/ItemsViewToggleLink';
import { PriceViewToggle } from '../../NewSearch/NewSearchBar/PriceViewToggle';
import { PriceViewMode } from '../types';
import { AdsSectionTitle } from './AdsSectionTitle';
import { ItemListBanner } from './ItemListBanner';
import {
  MobilePriceViewToggleRow,
  SectionRightBlock,
  sectionStylesMapView
} from './styled';

const searchListRef = React.createRef<HTMLDivElement | null>();
const EndlessSearchItems = withEndlessScroll(CardsLayout);

export interface RegularItemsSectionRef {
  refetch: () => Promise<ApolloQueryResult<SearchItemsQuery>>;
}

interface RegularItemsSectionProps {
  filter: { [key: string]: boolean | string };
  sorting?: string;
  onError: () => void;
}

export const RegularItemsSection = forwardRef<
  RegularItemsSectionRef,
  RegularItemsSectionProps
>(({ filter, sorting, onError }, ref) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isSmall, isLarge } = useScreenSize();
  const isClient = useClient();
  const { isBookmarked } = useBookmarks();
  const currentFormState = useSelector(selectSearchFormCurrentState);

  const isItemsAllPage = router.pathname === SEARCH_RESULT_ROUTES.ITEMS_ALL;
  const isSearchPage = router.pathname === '/[...search]';
  const isShowToggle = checkIsCategorySupportsPriceToggle(currentFormState);

  const { priceView, setPriceView, priceViewOptions } = useInitialPriceView(
    currentFormState?.category_id,
    sorting as ItemConnectionSort
  );
  const priceViewToggleOptions = priceViewOptions.map(option => ({
    id: option.id,
    name: t(option.translationKey)
  }));

  const [mapTotalCount, setMapTotalCount] = useState<number | null>(null);
  const isMapView = useGetIsMapView(MapViewQueryParam.itemsView);
  const isShowPriceViewToggle = isShowToggle && !isMapView;
  const isMobilePriceViewToggle = isShowPriceViewToggle && isSmall && isClient;

  const searchItemsQueryVariables = {
    first: 16,
    filter,
    sort: sorting || ItemConnectionSort.BumpedAtDesc
  };

  const {
    loading: searchItemsQueryLoading,
    data: searchItemsQueryData,
    error: searchItemsError,
    fetchMore,
    refetch: searchItemsQueryRefetch
  } = useQuery<SearchItemsQuery>(searchItemsQuery, {
    variables: searchItemsQueryVariables,
    ssr: false,
    notifyOnNetworkStatusChange: true,
    skip: isMapView
  });

  useImperativeHandle(ref, () => ({
    refetch: searchItemsQueryRefetch
  }));

  useEffect(() => {
    if (searchItemsError) {
      onError();
    }
  }, [searchItemsError]);

  const bannerStartIndex = useMemo(() => {
    switch (true) {
      case isSmall:
        return 5;
      case isLarge:
        return 11;
      default:
        return 8;
    }
  }, [isSmall, isLarge]);

  const itemsList =
    searchItemsQueryData?.itemsConnection?.edges?.map((item, currIndex) => (
      <React.Fragment key={item?.node?.id}>
        <ItemCard
          {...item?.node}
          inBookmarks={isBookmarked(item?.node?.id!)}
          priceView={priceView}
          stat="item_card_search_results"
        />
        {isSearchPage && currIndex === PARALLAX_GAP_LIST_INDEX && (
          <ParallaxGapBanner isSearchPage={isSearchPage} />
        )}
        <ItemListBanner
          currIndex={currIndex}
          bannerStartIndex={bannerStartIndex}
          isSmall={isSmall}
        />
      </React.Fragment>
    )) || [];

  const adsCount = isMapView
    ? mapTotalCount
    : searchItemsQueryData?.itemsConnection.totalCount;

  if (searchItemsQueryLoading && !itemsList.length) {
    return <SkeletonLoaders />;
  }

  const priceViewToggle = (
    <PriceViewToggle
      options={priceViewToggleOptions}
      selectedValue={priceView}
      onChange={setPriceView}
    />
  );

  return (
    <NewSection
      id="search-page-regular-items"
      title={
        <AdsSectionTitle isItemsAllPage={isItemsAllPage} adsCount={adsCount} />
      }
      headlineType="h3"
      cypress="search-page-regular-items-title"
      rightBlock={
        <SectionRightBlock>
          {isShowPriceViewToggle && !isMobilePriceViewToggle && priceViewToggle}

          {!isItemsAllPage && (
            <ItemsViewToggleLink
              isMapView={isMapView}
              query={MapViewQueryParam.itemsView}
              cypress="new-search-map-link"
              stat="new-search-map-link"
            />
          )}

          <SortingFilter
            submitOnChange
            isPerUnit={priceView === PriceViewMode.PerAcre}
            disabled={isMapView}
          />
        </SectionRightBlock>
      }
      styles={sectionStylesMapView(isMapView, isShowPriceViewToggle)}
    >
      {isMobilePriceViewToggle && (
        <MobilePriceViewToggleRow>{priceViewToggle}</MobilePriceViewToggleRow>
      )}
      {isMapView ? (
        <SearchResultsItemsMap
          filter={filter}
          onTotalCountChange={setMapTotalCount}
        />
      ) : (
        <EndlessSearchItems
          items={itemsList || []}
          loading={searchItemsQueryLoading}
          ref={searchListRef}
          isStopLoad={
            !!searchItemsQueryData &&
            !searchItemsQueryData.itemsConnection?.pageInfo?.hasNextPage
          }
          onLoadMore={() => {
            const moreSearchItemsQueryVariables = {
              ...searchItemsQueryVariables,
              cursor: searchItemsQueryData?.itemsConnection?.pageInfo?.endCursor
            };
            fetchMore({
              variables: moreSearchItemsQueryVariables
            });
          }}
        >
          {itemsList}
        </EndlessSearchItems>
      )}
    </NewSection>
  );
});

RegularItemsSection.displayName = 'RegularItemsSection';
